import { Panel, PageHeader } from "@/components/admin-shell";
import { apiRequest } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Mail, Phone } from "lucide-react";

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  orders: number;
  spent: number;
  joined: string;
}

export default function CustomersPage() {
  const {
    data: customers = [],
    isLoading,
    error,
  } = useQuery<Customer[]>({
    queryKey: ["customers"],
    queryFn: () => apiRequest<Customer[]>("/users?role=customer"),
  });

  const fmt = (n: number) => `৳${n.toLocaleString("en-BD")}`;

  // Calculate dynamic stats
  const totalCustomers = customers.length;
  const repeatBuyersCount = customers.filter((c) => c.orders > 1).length;
  const repeatPercentage =
    totalCustomers > 0 ? Math.round((repeatBuyersCount / totalCustomers) * 100) : 0;
  const totalSpent = customers.reduce((sum, c) => sum + c.spent, 0);
  const avgLifetimeValue = totalCustomers > 0 ? Math.round(totalSpent / totalCustomers) : 0;

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Every connoisseur of fragrance who has shopped with the house."
      />

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-gold">Loading customers...</div>
      ) : error ? (
        <div className="text-red-400">Error loading customers: {(error as Error).message}</div>
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <Panel className="p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold-soft">
                Total customers
              </div>
              <div className="mt-2 font-display text-3xl">{totalCustomers}</div>
            </Panel>
            <Panel className="p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold-soft">
                Repeat buyers
              </div>
              <div className="mt-2 font-display text-3xl">{repeatPercentage}%</div>
            </Panel>
            <Panel className="p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold-soft">
                Avg. lifetime value
              </div>
              <div className="mt-2 font-display text-3xl text-gold">{fmt(avgLifetimeValue)}</div>
            </Panel>
          </div>

          <Panel className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gold/15 bg-accent/30">
                  <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-gold-soft">
                    <th className="px-4 py-3 first:pl-5 last:pr-5">Customer</th>
                    <th className="px-4 py-3 first:pl-5 last:pr-5">Contact</th>
                    <th className="px-4 py-3 first:pl-5 last:pr-5 text-right">Orders</th>
                    <th className="px-4 py-3 first:pl-5 last:pr-5 text-right">Spent</th>
                    <th className="px-4 py-3 first:pl-5 last:pr-5">Joined</th>
                    <th className="px-4 py-3 first:pl-5 last:pr-5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gold/10">
                  {customers.map((c) => (
                    <tr key={c.id} className="hover:bg-accent/40">
                      <td className="px-4 py-4 first:pl-5 last:pr-5">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent font-display text-gold">
                            {c.name ? c.name[0] : "U"}
                          </div>
                          <div>
                            <div className="text-foreground">{c.name || "Anonymous User"}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">
                              {c.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 first:pl-5 last:pr-5">
                        <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Mail className="h-3 w-3" /> {c.email}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Phone className="h-3 w-3" /> {c.phone}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 first:pl-5 last:pr-5 text-right">{c.orders}</td>
                      <td className="px-4 py-4 first:pl-5 last:pr-5 text-right text-gold">
                        {fmt(c.spent)}
                      </td>
                      <td className="px-4 py-4 first:pl-5 last:pr-5 text-muted-foreground">
                        {c.joined}
                      </td>
                      <td className="px-4 py-4 first:pl-5 last:pr-5 text-right">
                        <span className="text-xs text-muted-foreground">Active Customer</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </>
  );
}
